import { AlertCircleIcon } from "Icons/AlertCircle";
import { CheckCircleIcon } from "Icons/CheckCircle";

import { useEffect } from "react";

import { css } from "@emotion/react";

import { MediaUploader } from "components/shared/library/MediaUploader";
import { Modal } from "components/shared/library/Modal";
import { SpinningIcon } from "components/shared/library/SpinningIcon";
import { Typography } from "components/shared/library/Typography";

import { useAlert } from "contexts/alertContext";

import { useUploadDocuments } from "apis/files";

import { black, green, purple, red, uiGray } from "constants/colors";
import {
  documentAcceptedFileTypes,
  documentFileTypeString,
  maxDocumentSizeInMb,
} from "constants/files";

import { FileItem } from "../shared/library/FileItem";

const style = {
  content: css({ display: "flex", flexDirection: "column", gap: 16 }),
  dropUpload: css({ height: 120 }),
  modal: { content: css({ maxHeight: "50vh" }) },
};

export const NewDocModal = ({
  isOpen,
  onClose,
  onDone,
  initialFiles,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
  initialFiles?: File[];
}) => {
  const { uploadFiles, files, error, reset } = useUploadDocuments();

  const { createAlert } = useAlert();

  useEffect(() => {
    if (error) createAlert("error", error);
  }, [error, createAlert]);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  useEffect(() => {
    if (initialFiles?.length) {
      uploadFiles(initialFiles);
    }
  }, [initialFiles, uploadFiles]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New documents"
      description="Add documents for enhanced context."
      onConfirm={() => (onDone ? onDone() : onClose())}
      isCancelable={false}
      confirmLabel="Done"
      customCss={style.modal}
      isConfirmDisabled={!files.length}
    >
      <div css={style.content}>
        <Typography color={black} size="M" weight="bold">
          Select a file to upload
        </Typography>
        <MediaUploader
          customLabel="Drag, drop or select files"
          customCss={{ dropUpload: style.dropUpload }}
          onChange={(files) =>
            Array.isArray(files) ? uploadFiles(files) : uploadFiles([files])
          }
          acceptedFileTypes={documentAcceptedFileTypes}
          fileTypeString={documentFileTypeString}
          maxSizeInMb={maxDocumentSizeInMb}
          multiple
        />
        {!!files?.length && (
          <>
            <Typography color={uiGray[90]} size="XS" weight="medium">
              Uploaded files
            </Typography>
            {files.map((file) => (
              <FileItem
                key={file.filename}
                name={file.filename}
                size={file.size}
                LeftComponent={() => (
                  <>
                    {file.status === "done" && (
                      <CheckCircleIcon
                        stroke={green[800]}
                        width={16}
                        height={16}
                      />
                    )}
                    {file.status === "error" && (
                      <AlertCircleIcon
                        stroke={red[600]}
                        width={16}
                        height={16}
                      />
                    )}
                    {(file.status === "not_started" ||
                      file.status === "in_progress") && (
                      <SpinningIcon
                        stroke={purple[600]}
                        width={16}
                        height={16}
                      />
                    )}
                  </>
                )}
              />
            ))}
          </>
        )}
      </div>
    </Modal>
  );
};
